import React from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Link as RouterLink } from 'react-router-dom';
import { ACTION_BLUE, ACTION_BLUE_HOVER } from '../../theme/theme';

// Shared between the two crumb states, which differ only in colour/weight.
const pillSx = {
  // inline-flex, not inline-block: an inline-block sits on the text baseline,
  // which is what pushed the separator chevron out of vertical centre.
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 1.5,
  fontFamily: 'IRANYekanXFaNum',
  fontSize: 13,
  padding: '5px',
  borderRadius: '10px',
  textDecoration: 'none',
  // A long app name should not push the trail onto a new line.
  maxWidth: 320,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export interface Crumb {
  label: string;
  /** Omit on the last crumb — the current page is not a link. */
  to?: string;
  /** Router state to carry along, for pages that read location.state. */
  state?: unknown;
}

interface BreadcrumbProps {
  items: Crumb[];
}

/**
 * Trail of ancestors for the current page, e.g.
 *   داشبورد ‹ هویت‌ها ‹ تنظیمات هویت
 *
 * Every crumb but the last is a link. In RTL the separator points left, which
 * is the direction of travel back up the trail.
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        direction: 'rtl',
        marginBottom: '10px',
      }}
    >
      <Breadcrumbs
        aria-label="مسیر صفحه"
        separator={
          <ChevronLeftIcon sx={{ fontSize: 16, color: '#A6AAB5' }} />
        }
        sx={{
          // The pills are taller than the bare chevron (padding + border), so
          // the default baseline alignment left the separator riding high.
          '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap', alignItems: 'center' },
          '& .MuiBreadcrumbs-separator': {
            marginX: '4px',
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          if (isLast || !item.to) {
            return (
              <Typography
                key={`${item.label}-${index}`}
                aria-current={isLast ? 'page' : undefined}
                sx={{
                  ...pillSx,
                  backgroundColor: '#ffffff',
                  color: '#606673',
                  border: '1px solid #d2d2d2',
                  fontWeight: 600,
                }}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={`${item.label}-${index}`}
              component={RouterLink}
              to={item.to}
              state={item.state}
              underline="none"
              sx={{
                ...pillSx,
                backgroundColor: ACTION_BLUE,
                color: '#ffffff',
                border: `1px solid ${ACTION_BLUE}`,
                fontWeight: 500,
                transition: 'background-color .2s ease, border-color .2s ease',
                '&:hover': {
                  backgroundColor: ACTION_BLUE_HOVER,
                  borderColor: ACTION_BLUE_HOVER,
                },
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;
